import React, { useEffect, useState, useCallback } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLocalized } from "@/lib/LanguageContext";
import { slugify, emptyPost } from "./blog/_shared";
import BlogList from "./blog/BlogList";
import BlogEditor from "./blog/BlogEditor";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useFieldErrors from "../hooks/useFieldErrors";
import { requiredField, withAnyLangRule } from "../lib/validation";

/**
 * What BlogPostBase (server.py) will and will not accept, stated on the fields.
 *
 * `slug` is the hard one: `min_length=1, max_length=160`, and it is DERIVED from the title
 * (both the editor's onChange and save() below slugify it), so an empty slug means either
 * "no title at all" or "a title slugify() reduces to nothing" — a real case, e.g. a title
 * made only of punctuation. Refusing it here replaces the opaque 422 the practitioner used
 * to get.
 *
 * The title is `withAnyLangRule`, not `frRequiredField`: `title_fr` is required as a KEY
 * but "" is accepted, and the localized readers fall back to the other language — so one
 * language really is enough for a post, unlike the catalogue's `name_fr`.
 */
const postSchema = withAnyLangRule(
  z.object({
    // `.optional()`: an absent language is exactly what withAnyLangRule judges (it treats a
    // non-string as blank); an aborted parse would put zod's own English "Required" on the
    // field instead of a translated key.
    title_fr: z.string().optional(),
    title_en: z.string().optional(),
    slug: requiredField(160),
  }),
  "title_fr",
  "title_en",
);

/**
 * BlogAdmin — orchestration only.
 * Avant B18 : 224L. Après : 65L + 3 fichiers (BlogList, BlogEditor, _shared).
 */
export default function BlogAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const loc = useLocalized();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/blog");
    setPosts(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    // The slug is derived here, BEFORE validating, and the derived value is what gets
    // judged and sent: validating `form` alone would refuse a post whose slug the editor
    // is about to compute from its title. The EN fallback matters for an English-only post
    // — its FR title is legitimately empty (see postSchema).
    const slug = form.slug || slugify(form.title_fr) || slugify(form.title_en);
    const payload = { ...form, slug };
    if (validate(postSchema, payload)) { toast.error(tv("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/blog", payload);
      else await api.put(`/admin/blog/${editing.id}`, payload);
      await load();
      setEditing(null);
    } catch (e) {
      toast.error(extractApiError(e, t("blog.saveError"), tv));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("blog.confirmDelete", { title: loc(p, "title") }))) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.deleteFailed"), tv));
    }
  };

  const uploadCover = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, cover_url: fullUrl }));
    } catch (e) {
      toast.error(extractApiError(e, t("media.toast.uploadFailed"), tv));
    }
  };

  const startNew = () => { setForm(emptyPost); setEditing("new"); clearAllErrors(); };
  const startEdit = (p) => { setForm({ ...emptyPost, ...p }); setEditing(p); clearAllErrors(); };

  return (
    <div data-testid="admin-blog">
      {!editing && <BlogList posts={posts} onEdit={startEdit} onDelete={remove} onNew={startNew} />}
      {editing && (
        <BlogEditor
          editing={editing}
          form={form}
          setForm={setForm}
          busy={busy}
          fieldErrors={fieldErrors}
          clearFieldError={clearFieldError}
          onSave={save}
          onCancel={() => { setEditing(null); clearAllErrors(); }}
          onUploadCover={uploadCover}
        />
      )}
    </div>
  );
}
